import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "layout__wrapper"
}
const _hoisted_2 = { class: "subscription-box" }
const _hoisted_3 = {
  key: 1,
  class: "layout__wrapper"
}
const _hoisted_4 = { class: "subscription-box" }
const _hoisted_5 = {
  key: 2,
  class: "layout__wrapper container"
}
const _hoisted_6 = ["href"]
const _hoisted_7 = {
  key: 3,
  class: "layout__wrapper container"
}
const _hoisted_8 = ["href"]

import { computed, onBeforeMount, provide, ref, watch } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRoute, useRouter } from 'vue-router'

import Layout from '@/app/components/Layout.vue' // @ is an alias to /src
import HeaderRoot from '@/app/components/header/Root.vue'
import BillingData from '@/app/components/billing/BillingData.vue'
import BillingDataPaid from '@/app/components/billing/BillingDataPaid.vue'
import BillingDataPaidAppStore from '@/app/components/billing/BillingDataPaidAppStore.vue'
import BillingDataFullAccess from '@/app/components/billing/BillingDataFullAccess.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'
import { assertNotNull } from '@/helpers/typing'
import { backend } from '@/services/backend'
import {
  interestBookKey,
  loadInterestBook,
  type InterestBook,
} from '@/helpers/billingSourceBook'
import { Sentry } from '@/services/sentry'
import {
  BillingView,
  createBilling,
  isAppleWeb,
  isGoogleWeb,
} from '@/models/billing'
import {
  iosExternalLinkPayment16218Variant,
  onboardingChecklist19055Experiment,
  onboardingChecklist19055Variant,
} from '@/services/ab'
import { checklist } from '@/models/checklist'


export default /*@__PURE__*/_defineComponent({
  __name: 'Billing',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const wait = useWait()

const billing = ref<Raw<BillingView> | null>(null)
// For checkout modal personalization (book stack UI).
const interestBook = ref<InterestBook | null>(null)
provide(interestBookKey, interestBook)

const isAppleWebBilling = ref(false)
const isGoogleWebBilling = ref(false)

onBeforeMount(async () => {
  void trackBillingFirstView()
  void loadInterestBookForBilling()
  wait.start('Loading Billing')
  await refreshBilling()
})

async function loadInterestBookForBilling(): Promise<void> {
  try {
    interestBook.value = await loadInterestBook(
      route.query.source_book as string | undefined,
    )
  } catch (error) {
    Sentry.captureException(error)
  }
}

async function refreshBilling(): Promise<void> {
  wait.start('Loading Billing')
  wait.end('Error Loading Billing')
  try {
    const response = await backend.getBilling()
    billing.value = await createBilling(response)
    isAppleWebBilling.value = await isAppleWeb(response)
    isGoogleWebBilling.value = await isGoogleWeb(response)
  } catch (error) {
    wait.start('Error Loading Billing')
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['billing-loading-error'])
      Sentry.captureException(error)
    })
  } finally {
    wait.end('Loading Billing')
  }
}

async function trackBillingFirstView(): Promise<void> {
  try {
    await backend.trackBillingFirstView()
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setFingerprint(['billing-first-view-track-error'])
      Sentry.captureException(error)
    })
  }
}

/**
 * Handles the redirect after subscription has been made.
 */
watch(
  () => billing.value?.subscribedAlertAndRedirect,
  async (value?: boolean /* , old: boolean */) => {
    if (value !== true) {
      return
    }

    let checklistExperimentVariant = onboardingChecklist19055Variant()
    if (!checklistExperimentVariant) {
      // This is one of 2 possible places where we can start this experiment.
      // The other place is in the Discover page. We start the experiment here
      // if the user didn't visit the Discover page before starting the trial.
      checklistExperimentVariant =
        await onboardingChecklist19055Experiment('trial')
    }

    // We used to have a six-second delay here, but it was removed after
    // exp_first_guide_activation_17817 was resolved.

    if (checklistExperimentVariant === 'B') {
      // Await the navigation before marking the checklist step as completed.
      // This prevents the notifiaction from being immediately dismissed from the
      // navigation.
      await router.push({ name: 'discover' })
      checklist()?.steps.start_trial?.addProgress(1)
    } else {
      router.push({ name: 'books' })
    }
  },
)

function isLoadingBilling(): boolean {
  return wait.is('Loading Billing')
}

function isErrorLoadingBilling(): boolean {
  return wait.is('Error Loading Billing')
}

const isFree = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return !billingValue.isPaidPlan
})

const isStripePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)

  const isAppleExternalBilling16218 =
    billingValue.billing_type === 'stripe' &&
    iosExternalLinkPayment16218Variant() === 'B'

  return (
    billingValue.isPaidPlan &&
    (!billingValue.isAppStore() || isAppleExternalBilling16218)
  )
})

const isApplePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return billingValue.isPaidPlan && billingValue.isAppStore()
})

return (_ctx: any,_cache: any) => {
  const _component_LoadingError = _resolveComponent("LoadingError")!

  return (_openBlock(), _createBlock(Layout, {
    class: "billing-layout",
    "show-upgrade-prompt-modal": false
  }, {
    default: _withCtx(() => [
      _createVNode(HeaderRoot, { title: "Billing" }),
      (isLoadingBilling())
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("section", _hoisted_2, [
              _createVNode(LoadingSpinner)
            ])
          ]))
        : (isErrorLoadingBilling() || !billing.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("section", _hoisted_4, [
                _createVNode(_component_LoadingError)
              ])
            ]))
          : (isAppleWebBilling.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(ContentWrapper, null, {
                  default: _withCtx(() => [
                    _cache[0] || (_cache[0] = _createTextVNode(" You are subscribed with Apple in-app purchase subscription. Please use native iOS app or ", -1)),
                    _createElementVNode("a", {
                      href: billing.value.paymentProviderSettingsUrl,
                      target: "_blank",
                      class: "provider-settings-link"
                    }, " Apple subscription interface ", 8, _hoisted_6),
                    _cache[1] || (_cache[1] = _createTextVNode(" to manage your subscription. ", -1))
                  ]),
                  _: 1
                })
              ]))
            : (isGoogleWebBilling.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(ContentWrapper, null, {
                    default: _withCtx(() => [
                      _cache[2] || (_cache[2] = _createTextVNode(" You are subscribed through Google Play. Please use the native Android app or the ", -1)),
                      _createElementVNode("a", {
                        href: billing.value.paymentProviderSettingsUrl,
                        target: "_blank",
                        class: "provider-settings-link"
                      }, " Google Play settings ", 8, _hoisted_8),
                      _cache[3] || (_cache[3] = _createTextVNode(" to manage your subscription. ", -1))
                    ]),
                    _: 1
                  })
                ]))
              : (billing.value.hasFullAccess)
                ? (_openBlock(), _createBlock(BillingDataFullAccess, { key: 4 }))
                : (isFree.value)
                  ? (_openBlock(), _createBlock(BillingData, {
                      key: 5,
                      billing: billing.value
                    }, null, 8, ["billing"]))
                  : (isStripePaid.value)
                    ? (_openBlock(), _createBlock(BillingDataPaid, {
                        key: 6,
                        billing: billing.value
                      }, null, 8, ["billing"]))
                    : (isApplePaid.value)
                      ? (_openBlock(), _createBlock(BillingDataPaidAppStore, {
                          key: 7,
                          billing: billing.value
                        }, null, 8, ["billing"]))
                      : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})